import { type ErrorComponentProps } from '@sitecore-jss/sitecore-jss-react-forms';

import { FC, useEffect, useRef } from 'react';

import { useFormSubmitContext } from './Context/FormSubmitContext';

/**
 * Overrides the built-in Sitecore forms error component.
 * @see [Original Default Error Implementation](https://github.com/Sitecore/jss/blob/dev/packages/sitecore-jss-react-forms/src/components/default-error.tsx)
 *
 * @param props - `FormErrorSummary` properties.
 * @returns A JSX element.
 */
export const FormErrorSummary: FC<ErrorComponentProps> = (props) => {
  const { fieldErrors, formErrors } = props;
  const { focusRequestId } = useFormSubmitContext();

  const handledFocusRequestRef = useRef(0);
  const formErrorSummaryRef = useRef<HTMLDivElement>(null);
  const invalidClassName = 'invalid';

  useEffect(() => {
    // Exit if focus request was already handled
    if (focusRequestId === handledFocusRequestRef.current) {
      return;
    }

    const formElement = formErrorSummaryRef.current?.closest('form');
    const firstInvalidElement = formElement?.querySelector<HTMLElement>('[aria-invalid="true"]');

    // Exit if there are no invalid elements
    if (!firstInvalidElement) {
      return;
    }

    // Update focus request reference
    handledFocusRequestRef.current = focusRequestId;

    // Respects user motion preference
    const matchMediaQuery = '(prefers-reduced-motion: reduce)';
    const prefersReducedMotion = window.matchMedia(matchMediaQuery).matches;

    // Focus and scroll to first invalid element
    firstInvalidElement.focus({ preventScroll: true });
    firstInvalidElement.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
  }, [fieldErrors.length, focusRequestId, formErrors.length]);

  return (
    <div className="form-errors" ref={formErrorSummaryRef}>
      {formErrors.map((error, index) => (
        <div className={invalidClassName} key={`formError-${index}`}>
          {error}
        </div>
      ))}
      {fieldErrors.map((error, index) => (
        <div className={invalidClassName} key={`fieldError-${index}`}>
          {error.state.errors.map((message) => (
            <div key={message}>{message}</div>
          ))}
        </div>
      ))}
    </div>
  );
};
